import type { DiffSidebarCategoryType, DiffSidebarPageType, ReadMergeType } from '@readme/api/src/mappings/merge/types';

/**
 * Declares the enum list of sections that are branchable and can contain diffs
 * between itself and its parent version. Note that this intentionally contains
 * more granular sections than our `getMergeStatus` endpoint returns.
 */
export type BranchDiffSection =
  | 'apiDefinitions'
  | 'customComponents'
  | 'customContent'
  | 'customPages'
  | 'guides'
  | 'recipes'
  | 'reference';

/**
 * Diff status representation, which is largely based off our merge status
 * response but with the `sidebar` field required.
 */
export type BranchDiffStatus = ReadMergeType<'expanded'>['data'];

/**
 * Representation of a single page element from the diff status representation
 */
export type BranchDiffPage = ReadMergeType<'expanded'>['data']['sidebar'][number]['pages'][number];

/**
 * Finds the first diff page within the provided diff `sidebar`. Optionally, if
 * a `predicate` is provided it will only return the first page that matches the
 * predicate's criteria and returns `true`. If no page is found, returns `null`.
 */
export function findDiffPage(
  entities: (DiffSidebarCategoryType | DiffSidebarPageType)[],
  predicate?: (page: DiffSidebarPageType) => boolean,
): DiffSidebarPageType | null {
  for (let n = 0; n < entities.length; n += 1) {
    const entity = entities[n];
    if ('slug' in entity) {
      if (predicate?.(entity)) {
        // if a predicate was provided and this page matches that predicate's
        // criteria, then return it
        return entity;
      } else if (!predicate) {
        // if no predicate was provided, just return this first page
        return entity;
      }
    }
    if ('pages' in entity) {
      const result = findDiffPage(entity.pages, predicate);
      if (result) return result;
    }
  }

  return null;
}

/**
 * Finds the first diff page within the provided diff `sidebar` that matches the
 * given `slug`. Returns `null` if no matching page exists.
 */
export function findDiffPageBySlug(
  entities: (DiffSidebarCategoryType | DiffSidebarPageType)[],
  slug: string,
): DiffSidebarPageType | null {
  if (!slug) return null;

  return findDiffPage(entities, page => page.slug === slug);
}
